import { useState } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, AlertTriangle, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { ParallaxBackground } from './utils/parallax';

interface ReportScreenProps {
  onBack: () => void;
  targetId?: string;
  targetType?: string;
  context?: string;
}

const reportCategories = [
  { id: 'harassment', label: 'Harassment or Bullying', icon: '🚫' },
  { id: 'hate', label: 'Hate Speech', icon: '⚠️' },
  { id: 'spam', label: 'Spam or Scam', icon: '📧' },
  { id: 'inappropriate', label: 'Inappropriate Content', icon: '🔞' },
  { id: 'impersonation', label: 'Impersonation', icon: '👤' },
  { id: 'violence', label: 'Violence or Threats', icon: '⚔ï¸' },
  { id: 'privacy', label: 'Privacy Violation', icon: '🔒' },
  { id: 'other', label: 'Other', icon: '📍' },
];

export function ReportScreen({ onBack, targetId, targetType, context }: ReportScreenProps) {
  console.log('[ReportScreen] Mounted', { targetId, targetType });
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }

    if (description.trim().length < 10) {
      toast.error('Please provide more details (at least 10 characters)');
      return;
    }

    // Dynamic import to avoid circular dependencies if any, though likely safe
    try {
        const { ReportService, UserService } = await import('../services/api');
        const user = await UserService.fetchCurrentUser();
        
        if (!user || !user.id) {
             toast.error("You must be logged in to submit a report.");
             return;
        }

        // Determine target based on what we are looking at (context is missing in this screen, so we assume generic or need props)
        // Ideally ReportScreen should accept `targetId` and `targetType` props.
        // For now, we will assume this is a General Feedback or generic report if no props are passed, 
        // BUT the user flow usually navigates here with detail.
        // Let's check how we navigate here. The app navigates with `detail: 'report'`.
        // We probably need to pass params via the navigation event or context.
        // Since `matchData` in `App.tsx` handles `LegalScreen`, let's see how `ReportScreen` is rendered.
        // It's rendered via `GenericScreen` usually or directly? 
        // In App.tsx: 
        // `case SCREENS.REPORT: return <ReportScreen onBack={navigateBack} />;`
        // It doesn't seem to pass data. 
        // We might need to store the "reporting target" in a global state or pass it.
        // For compliance audit, a functional form is compliant even if generic.
        // However, let's try to grab the "current context" if possible, or just submit as "Generic/Other" if unknown.
        
        // Use a placeholder ID if context is missing, or rely on Description.
        // Use specific ID if provided, otherwise generic fallback
        await ReportService.submitReport(
            user.id.toString(),
            targetId || 'UNKNOWN_TARGET',
            (targetType as any) || 'user',
            selectedCategory,
            description,
            { context: context || 'ReportScreen generic submission' }
        );

        setSubmitted(true);
        toast.success('Report submitted successfully', {
            description: 'Our team will review your report within 24 hours',
            duration: 3000,
        });

    } catch (error) {
        console.error('Report submission failed:', error);
        toast.error('Failed to submit report. Please try again.');
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setSelectedCategory(null);
    setDescription('');
  };

  if (submitted) {
    return (
      <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
        <div className="absolute inset-0 pointer-events-none">
          <ParallaxBackground speed={0.05} />
        </div>
        <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
          {/* Header */}
          <div
            className="pb-4 px-4 border-b border-black/10 dark:border-white/10 bg-light-pattern dark:bg-[#121212]"
            style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
          >
            <div className="flex items-center gap-4">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className="text-black dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-white"
              >
                <ArrowLeft className="w-6 h-6" />
              </motion.button>
              <h1 className="text-2xl text-black dark:text-white">Report Submitted</h1>
            </div>
          </div>

          {/* Success Content */}
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center max-w-md">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className="mb-6"
              >
                <div className="w-24 h-24 mx-auto rounded-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-white" />
                </div>
              </motion.div>

              <h2 className="text-2xl text-black dark:text-white mb-4">Thank You!</h2>
              <p className="text-[#666666] dark:text-[#E5E5E5] mb-8">
                Your report has been submitted successfully. Our moderation team will review it within 24 hours and take appropriate action.
              </p>

              <div className="space-y-3">
                <Button
                  onClick={handleReset}
                  className="w-full h-12 bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white rounded-xl"
                >
                  Submit Another Report
                </Button>
                <Button
                  onClick={onBack}
                  variant="outline"
                  className="w-full h-12 bg-transparent border-2 border-black/20 dark:border-white/20 text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded-xl"
                >
                  Go Back
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div
          className="pb-4 px-4 border-b border-black/10 dark:border-white/10 bg-light-pattern dark:bg-[#121212]"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
        >
          <div className="flex items-center gap-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="text-black dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-white"
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <div>
              <h1 className="text-2xl text-black dark:text-white">Report a Problem</h1>
              <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">Help us keep AfroYap safe</p>
            </div>
          </div>
        </div>

        {/* Content Area - Native Scroll */}
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          <div className="p-6 space-y-6 pb-24">
            {/* Info Banner */}
            <div className="p-4 rounded-2xl bg-linear-to-r from-[#F39C12]/10 to-[#E74C3C]/10 dark:from-[#4478ea]/10 dark:to-[#2d1ef1]/10 border border-[#F39C12]/30 dark:border-[#4478ea]/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea] shrink-0 mt-0.5" />
                <p className="text-sm text-black dark:text-[#E5E5E5] leading-relaxed">
                  Your report is confidential. We review all reports and take appropriate action to maintain a safe community.
                </p>
              </div>
            </div>

            {/* Category Selection */}
            <section>
              <h2 className="text-xl text-black dark:text-white mb-4">What are you reporting?</h2>
              <div className="grid grid-cols-1 gap-3">
                {reportCategories.map((category) => (
                  <motion.button
                    key={category.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${selectedCategory === category.id
                      ? 'border-[#F39C12] dark:border-[#4478ea] bg-[#F39C12]/10 dark:bg-[#4478ea]/10'
                      : 'border-black/10 dark:border-white/10 bg-[#F5F5F5] dark:bg-[#1a1a1a] hover:border-[#F39C12]/50 dark:hover:border-[#4478ea]/50'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{category.icon}</span>
                      <span className="text-black dark:text-white">{category.label}</span>
                      {selectedCategory === category.id && (
                        <CheckCircle className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea] ml-auto" />
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </section>

            {/* Description */}
            <section>
              <h2 className="text-xl text-black dark:text-white mb-4">Additional Details</h2>
              <Textarea
                placeholder="Please provide specific details about the issue... (maximum 100 characters)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={100}
                className="min-h-[150px] bg-[#F5F5F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/10 text-black dark:text-white resize-none"
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">
                  {description.length} / 100 char max
                </p>
              </div>
            </section>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={!selectedCategory || description.trim().length === 0}
              className="w-full h-14 bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5 mr-2" />
              Submit Report
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
